// frontend/src/components/admin/ActivityPerson.js
// Admin ▸ Activity ▸ one person (/admin/activity/:userId, audit.read): wireframe
// wf-admin-activity 3 (YES-316), call map section 20. One row per session with how it ended and the
// coding done inside it. No per-item rows: per-item times belong to the run export.
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { PERIODS, periodQuery, getPersonSessions, formatDuration, formatWhen, downloadCsv } from '../../services/activityService';
import { BeforeSessions, Filter, LoadFailed, RoleChip, Tiles } from './Activity';

const HEAD = { fontSize: 12, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', whiteSpace: 'nowrap' };

// How a session ended: sign-out, idle sign-out (YES-315), no sign-out (tab closed), or still going
const ENDINGS = {
    signed_out: { label: 'Signed out', sx: { bgcolor: 'grey.200', color: 'text.secondary' } },
    idle: { label: 'Idle sign-out', sx: { bgcolor: '#fff4d6', color: '#8a5a00' } },
    none: { label: 'No sign-out', sx: { bgcolor: '#fdecea', color: 'error.dark' } },
    open: { label: 'Signed in now', sx: { bgcolor: '#e6f4ea', color: 'success.dark' } },
};

const Tile = ({ label, value, sub }) => (
    <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 180 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>{label}</Typography>
        <Typography variant="h5" component="p" sx={{ fontWeight: 700, mt: 0.5 }}>{value}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Paper>
);

const time = (iso) => new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });

const ActivityPerson = () => {
    const { userId } = useParams();
    const [period, setPeriod] = useState('7d');
    const [data, setData] = useState(null);
    const [failed, setFailed] = useState(null);

    const load = useCallback(async () => {
        setFailed(null);
        try {
            const range = periodQuery(period);
            setData({ ...(await getPersonSessions(userId, range)), from: range.from });
        } catch (err) {
            setFailed(err.response?.status === 404 ? 404 : 'error');
        }
    }, [userId, period]);

    useEffect(() => { load(); }, [load]);

    const back = (
        <Button component={RouterLink} to="/admin/activity" size="small" sx={{ mb: 1, ml: -1 }}>← Activity</Button>
    );

    if (failed === 404) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4 }}>
                {back}
                <Typography variant="h5" component="h1">No such account</Typography>
                <Typography color="text.secondary">It may have been deleted. Its events stay in the event log.</Typography>
            </Container>
        );
    }

    const t = data?.totals;
    const download = () => downloadCsv(
        `adas-activity-${data.user.name.replace(/\W+/g, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.csv`,
        ['started', 'ended', 'how_it_ended', 'length_minutes', 'items_done', 'lessons_submitted', 'last_event', 'last_event_at'],
        data.sessions.map((s) => [s.startedAt, s.endedAt, s.cause, Math.round(s.lengthMs / 60000), s.itemsDone, s.lessonsSubmitted, s.lastEvent?.action, s.lastEvent?.at]),
    );

    return (
        <Container maxWidth="lg" sx={{ mt: 3, mb: 6 }}>
            {back}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>{data ? data.user.name : ' '}</Typography>
                {data && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <RoleChip role={data.user.role} />
                        <Typography variant="body2" color="text.secondary">{PERIODS.find((p) => p.value === period).label}</Typography>
                    </Box>
                )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2 }}>
                <Filter label="Period" value={period} onChange={setPeriod} options={PERIODS} width={170} />
                <Box sx={{ flex: 1 }} />
                <Button variant="outlined" disabled={!data?.sessions?.length} onClick={download}>Download CSV</Button>
            </Box>

            {failed && <Paper variant="outlined"><LoadFailed onRetry={load} /></Paper>}
            {!failed && !data && <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>}
            {!failed && data && (
                <>
                    <Tiles>
                        <Tile label="Sessions" value={t.sessions ?? '—'} sub={t.medianSessionMs != null ? `median ${formatDuration(t.medianSessionMs)}` : null} />
                        <Tile label="Time signed in" value={formatDuration(t.signedInMs)} />
                        <Tile label="Items done" value={t.itemsDone} sub={t.lessonsSubmitted ? `${t.lessonsSubmitted} lessons submitted` : null} />
                        <Tile label="Items / hour" value={t.itemsPerHour ?? '—'} />
                    </Tiles>
                    <BeforeSessions since={data.sessionsSince} from={data.from} />
                    <Paper variant="outlined">
                        {data.sessions.length === 0 ? (
                            <Box sx={{ p: 4 }}>
                                <Typography variant="h6" gutterBottom>No sessions in this period</Typography>
                                <Typography color="text.secondary">Try a longer period.</Typography>
                            </Box>
                        ) : (
                            <TableContainer>
                                <Table aria-label="Sessions">
                                    <TableHead>
                                        <TableRow>
                                            {['Started', 'Ended', 'How it ended', 'Length', 'Items', 'Lessons', 'Last event'].map((h) => (
                                                <TableCell key={h} sx={HEAD}>{h}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.sessions.map((s) => (
                                            <TableRow key={s.startedAt}>
                                                <TableCell>{formatWhen(s.startedAt)}</TableCell>
                                                <TableCell>{s.endedAt ? `${time(s.endedAt)}${s.cause === 'none' ? ' (last activity)' : ''}` : '—'}</TableCell>
                                                <TableCell><Chip size="small" label={ENDINGS[s.cause]?.label || s.cause} sx={ENDINGS[s.cause]?.sx} /></TableCell>
                                                <TableCell>{formatDuration(s.lengthMs)}</TableCell>
                                                <TableCell>{s.itemsDone || '—'}</TableCell>
                                                <TableCell>{s.lessonsSubmitted || '—'}</TableCell>
                                                <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                                                    {s.lastEvent ? `${s.lastEvent.action} ${time(s.lastEvent.at)}` : '—'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Paper>
                    {data.more && (
                        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
                            Showing the latest 100 sessions. Pick a shorter period to see earlier ones.
                        </Typography>
                    )}
                </>
            )}
        </Container>
    );
};

export default ActivityPerson;
